import { AttributesSelector } from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import { PositionIdsFilter } from './PositionIdsFilter'
import { ApprovalStatusFilter } from './ApprovalStatusFilter/ApprovalStatusFilter'
import { approvalStatuses } from './ApprovalStatusFilter/constants'
import { PrescreenStatusFilter } from './PrescreenStatusFilter/PrescreenStatusFilter'
import { prescreenStatuses } from './PrescreenStatusFilter/constants'

export const PositionFilters = () => {
  const methods = useFormContext<FiltersSchema>()
  const position = methods.watch('position')

  const resetToDefaultValues = () => {
    methods.reset({
      ...methods.getValues(),
      position: {
        ...methods.getValues('position'),
        positionIds: [],
        status: 'all',
        prescreenStatus: 'all',
      },
    })
  }

  const resetPositionIds = () => {
    methods.reset({
      ...methods.getValues(),
      position: {
        ...methods.getValues('position'),
        positionIds: [],
      },
    })
  }

  const resetStatus = () => {
    methods.reset({
      ...methods.getValues(),
      position: {
        ...methods.getValues('position'),
        status: 'all',
      },
    })
  }

  const resetPrescreenStatus = () => {
    methods.reset({
      ...methods.getValues(),
      position: {
        ...methods.getValues('position'),
        prescreenStatus: 'all',
      },
    })
  }

  const numberOfPositionIdsSelected = position.positionIds?.length ?? 0
  const hasStatus = position.status !== 'all'
  const hasPrescreenStatus = position.prescreenStatus !== 'all'

  const selectedPositionAttributes = useMemo(() => {
    const fields: string[] = []
    const pluralRules = new Intl.PluralRules('en-US')

    if (numberOfPositionIdsSelected > 0) {
      const plural = pluralRules.select(numberOfPositionIdsSelected)
      const suffix = plural === 'one' ? 'Position' : 'Positions'
      fields.push(`${numberOfPositionIdsSelected} ${suffix}`)
    }

    if (hasStatus) {
      const found = approvalStatuses.find(
        (status) => status.value === position.status
      )
      if (found) {
        fields.push(found.label)
      }
    }

    if (hasPrescreenStatus) {
      const found = prescreenStatuses.find(
        (status) => status.value === position.prescreenStatus
      )
      if (found) {
        fields.push(found.label)
      }
    }

    return fields
  }, [
    numberOfPositionIdsSelected,
    hasStatus,
    position.status,
    hasPrescreenStatus,
    position.prescreenStatus,
  ])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="Position"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedPositionAttributes}
        onClearAll={resetToDefaultValues}
        selectedAttributeLimit={5}
        groups={[
          <AttributesSelector.Group
            key="position-ids"
            title="Position IDs"
            numberOfSelectedAttributes={numberOfPositionIdsSelected}
            onClearAll={resetPositionIds}
          >
            <PositionIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="approval-status"
            title="Approval Status"
            numberOfSelectedAttributes={hasStatus ? 1 : 0}
            onClearAll={resetStatus}
          >
            <ApprovalStatusFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="prescreen-status"
            title="Prescreen Status"
            numberOfSelectedAttributes={hasPrescreenStatus ? 1 : 0}
            onClearAll={resetPrescreenStatus}
          >
            <PrescreenStatusFilter />
          </AttributesSelector.Group>,
        ]}
      />
    </FormProvider>
  )
}
